import {
  ComponentPropsWithoutRef,
  forwardRef,
  Ref,
  useContext,
  useEffect,
  useRef,
  useState,
  UIEvent,
} from "react";
import { createPortal } from "react-dom";
import cx from "classnames";
import styles from "./Page.module.scss";
import { PageContext } from "./PageContext";
import { useMergeRefs } from "../../hooks";

/**
 * Props for the PageContent component
 * @extends ComponentPropsWithoutRef<"section">
 */
export type PageContentProps = ComponentPropsWithoutRef<"section">;

/**
 * PageContent component for the main content area of a page.
 *
 * Features:
 * - Renders as a section element inside Page's main landmark
 * - Virtual scrolling support for long content
 * - Automatic scroll synchronization
 * - Responsive design
 * - Portals into Page's content wrapper via context
 * - Scroll position management
 *
 * @example
 * <Page.Content>
 *   <Text>Main page content goes here</Text>
 *   <Button>Action Button</Button>
 * </Page.Content>
 */
export const PageContent = forwardRef(
  (props: PageContentProps, ref: Ref<HTMLElement>) => {
    const { children, className, ...rest } = props;
    const classNames = cx([styles["content"]], className);
    const sectionRef = useRef<HTMLElement>(null);
    const virtualScrollRef = useRef<HTMLDivElement>(null);
    const blocker = useRef(true);
    const [leftOffset, setLeftOffset] = useState(
      (sectionRef.current?.offsetLeft ?? 0) +
        (sectionRef.current?.offsetWidth ?? 0) -
        9,
    );
    const [contentHeight, setContentHeight] = useState(
      sectionRef.current?.offsetHeight,
    );

    const { contentWrapper } = useContext(PageContext);

    useEffect(() => {
      if (typeof window === "undefined") return;
      if (!blocker.current) return;
      function updatePosition() {
        const newValue = window.scrollY;

        virtualScrollRef.current?.scrollTo(0, newValue ?? 0);
      }
      window?.addEventListener("scroll", updatePosition, { passive: true });

      return () => {
        window?.removeEventListener("scroll", updatePosition);
      };
    }, []);

    useEffect(() => {
      if (sectionRef.current) {
        const wrapperParent = sectionRef.current.parentElement;
        const isWrapped =
          wrapperParent?.classList.contains(styles["content-wrapper"]) ?? false;
        const heightTarget = isWrapped ? wrapperParent! : sectionRef.current;

        const observer = new ResizeObserver(() => {
          if (!sectionRef.current) return;
          setLeftOffset(
            sectionRef.current.offsetLeft + sectionRef.current.offsetWidth - 9,
          );
          const offsetStr = getComputedStyle(sectionRef.current)
            .getPropertyValue("--offset")
            .trim();
          const offset = parseFloat(offsetStr) || 0;
          const rawHeight = document.documentElement.scrollHeight - offset;
          const safeHeight = Number.isFinite(rawHeight)
            ? Math.max(0, rawHeight)
            : 0;
          setContentHeight(safeHeight);
        });

        observer.observe(sectionRef.current);
        if (isWrapped) {
          observer.observe(heightTarget);
        }

        return () => {
          observer.disconnect();
        };
      }
    }, [contentWrapper]);

    function scrollBody(e: UIEvent<HTMLElement>) {
      if (typeof window === "undefined") return;
      if (blocker.current) return;
      window.scrollTo(0, (e.target as HTMLDivElement).scrollTop);
    }

    const section = (
      <section
        className={classNames}
        ref={useMergeRefs([sectionRef, ref])}
        {...rest}
      >
        <div className={styles["content-inner"]}>{children}</div>
        <div
          className={styles["virtual-scroll-container"]}
          ref={virtualScrollRef}
          style={{
            left: leftOffset,
          }}
          onScrollCapture={scrollBody}
          onMouseEnter={() => {
            blocker.current = false;
          }}
          onMouseLeave={() => {
            blocker.current = true;
          }}
        >
          <div style={{ width: 1, height: contentHeight }} />
        </div>
      </section>
    );

    if (contentWrapper) {
      return createPortal(section, contentWrapper);
    }

    if (contentWrapper === undefined) {
      return section;
    }

    return null;
  },
);

PageContent.displayName = "PageContent";
